import { useMutation, useQuery } from '@apollo/client';
import {
    MUTATION_BULK_UPDATE_BUSINESS_ENTITY,
    MUTATION_CREATE_BUSINESS_ENTITY,
    MUTATION_SET_PREFERRED_BUSINESS_ENTITIES,
    MUTATION_UPDATE_BUSINESS_ENTITY
} from 'graphql/mutations/businessEntities';
import { QUERY_FIND_MANY_BUSINESS_ENTITIES, QUERY_GET_PREFERRED_BUSINESS_ENTITIES } from 'graphql/queries/businessEntities';
import { useMemo } from 'react';
import { BulkUpdateBusinessEntities, BulkUpdateBusinessEntitiesVariables } from '../components/BulkEditSidePanel/types';
import {
    CreateBusinessEntity,
    CreateBusinessEntityVariables,
    FindManyBusinessEntities,
    FindManyBusinessEntitiesVariables,
    GetPreferredBusinessEntities,
    UpdateBusinessEntity,
    UpdateBusinessEntityVariables,
    SetPreferredBusinessEntities,
    SetPreferredBusinessEntitiesVariables
} from '../types';

export const useBusinessEntityMutations = () => {
    const { loading: loadingPreferredBusinessEntities, data: preferredBusinessEntitiesData } = useQuery<GetPreferredBusinessEntities>(
        QUERY_GET_PREFERRED_BUSINESS_ENTITIES
    );

    const preferredEntityIds = useMemo(
        () => preferredBusinessEntitiesData?.getPreferredBusinessEntities.preferredEntityIds,
        [preferredBusinessEntitiesData]
    );

    const [createBusinessEntity, { loading: loadingCreateBusinessEntity }] = useMutation<
        CreateBusinessEntity,
        CreateBusinessEntityVariables
    >(MUTATION_CREATE_BUSINESS_ENTITY, {
        update(cacheUpdate, { data: newData }) {
            const existingData = cacheUpdate.readQuery<FindManyBusinessEntities, FindManyBusinessEntitiesVariables>({
                query: QUERY_FIND_MANY_BUSINESS_ENTITIES,
                variables: { data: { enabled: true } }
            });
            if (existingData && newData && newData.createBusinessEntity.enabled) {
                cacheUpdate.writeQuery<FindManyBusinessEntities, FindManyBusinessEntitiesVariables>({
                    query: QUERY_FIND_MANY_BUSINESS_ENTITIES,
                    variables: { data: { enabled: true } },
                    data: {
                        findManyBusinessEntities: [...existingData.findManyBusinessEntities, newData.createBusinessEntity]
                    }
                });
            }
        }
    });

    const [updateBusinessEntity, { loading: loadingUpdateBusinessEntity }] = useMutation<
        UpdateBusinessEntity,
        UpdateBusinessEntityVariables
    >(MUTATION_UPDATE_BUSINESS_ENTITY, {
        update(cacheUpdate, { data: newData }) {
            const existingData = cacheUpdate.readQuery<FindManyBusinessEntities, FindManyBusinessEntitiesVariables>({
                query: QUERY_FIND_MANY_BUSINESS_ENTITIES,
                variables: { data: { enabled: true } }
            });
            if (existingData && newData) {
                const updatedEntity = newData.updateBusinessEntity;
                const newList = updatedEntity.enabled
                    ? existingData.findManyBusinessEntities.map((el) => (el.id === updatedEntity.id ? newData.updateBusinessEntity : el))
                    : existingData.findManyBusinessEntities.filter((el) => el.id !== newData.updateBusinessEntity.id);

                cacheUpdate.writeQuery<FindManyBusinessEntities, FindManyBusinessEntitiesVariables>({
                    query: QUERY_FIND_MANY_BUSINESS_ENTITIES,
                    variables: { data: { enabled: true } },
                    data: {
                        findManyBusinessEntities: newList
                    }
                });
            }
        }
    });

    const [bulkUpdateBusinessEntities, { loading: loadingBulkUpdateBusinessEntities }] = useMutation<
        BulkUpdateBusinessEntities,
        BulkUpdateBusinessEntitiesVariables
    >(MUTATION_BULK_UPDATE_BUSINESS_ENTITY, {
        refetchQueries: [
            {
                query: QUERY_FIND_MANY_BUSINESS_ENTITIES,
                variables: { data: { enabled: true } }
            }
        ],
        awaitRefetchQueries: false,
        errorPolicy: 'all'
    });

    const [setPreferredBusinessEntities, { loading: loadingSetPreferredBusinessEntities }] = useMutation<
        SetPreferredBusinessEntities,
        SetPreferredBusinessEntitiesVariables
    >(MUTATION_SET_PREFERRED_BUSINESS_ENTITIES);

    // Functions to mark/unmark entity as used by tenant
    const markEntityAsUsedByTenant = async (entityId: number) => {
        // Add the new entityId to the existing list if it's not already present
        const existingIds = preferredEntityIds || [];
        const entityIdsToSet = existingIds.includes(entityId) ? existingIds : [...existingIds, entityId];

        await setPreferredBusinessEntities({
            variables: {
                data: {
                    preferredEntityIds: entityIdsToSet
                }
            },
            refetchQueries: [
                {
                    query: QUERY_GET_PREFERRED_BUSINESS_ENTITIES
                }
            ]
        });
    };

    const unmarkEntityAsUsedByTenant = async (entityId: number) => {
        const newPreferredEntityIds = preferredEntityIds?.filter((id) => id !== entityId) || [];

        await setPreferredBusinessEntities({
            variables: {
                data: {
                    preferredEntityIds: newPreferredEntityIds
                }
            },
            refetchQueries: [
                {
                    query: QUERY_GET_PREFERRED_BUSINESS_ENTITIES
                }
            ]
        });
    };

    const loading =
        loadingCreateBusinessEntity ||
        loadingUpdateBusinessEntity ||
        loadingBulkUpdateBusinessEntities ||
        loadingSetPreferredBusinessEntities ||
        loadingPreferredBusinessEntities;

    return {
        createBusinessEntity,
        updateBusinessEntity,
        bulkUpdateBusinessEntities,
        loading,
        setPreferredBusinessEntities,
        markEntityAsUsedByTenant,
        unmarkEntityAsUsedByTenant,
        preferredEntityIds
    };
};
